// The event tracker on the Overview (Lane 1440, Ryan's mockup v1): one row of steps built
// only from saved state (the server's `invitations.tracker`), then one "Next step" line
// with one button that goes where that step is done. Every count is the server's.
import React from 'react';
import { formatDueAt } from '../sourcing-portal/portalView';
import { formatMoney } from './sourcingApi';

function shortDay(day) {
  if (!day) return '';
  const d = new Date(`${String(day).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function shortAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// A change against today as the Overview's "Change against today" tile writes it: a saving
// as an amount, a cost increase in brackets.
export function changeText(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n >= 0 ? formatMoney(Math.abs(n)) : `(${formatMoney(Math.abs(n))})`;
}

export function subText(sub) {
  if (!sub) return '';
  // Wingman's recommendation against today (the tile's own figure), on the Awarded step.
  if (sub.money !== undefined) {
    const money = changeText(sub.money);
    if (!money) return '';
    const lines = sub.lines ? `, on ${sub.lines.known} of ${sub.lines.of} parts` : '';
    return `${money} ${sub.text}${lines}${sub.earlier ? ' (earlier)' : ''}`;
  }
  // A date never splits ("Planned" over "Nov 10", never "Nov" over "10").
  const date = (sub.day ? shortDay(sub.day) : sub.at ? shortAt(sub.at) : '').replace(/ /g, '\u00a0');
  return [sub.text, date].filter(Boolean).join(' ');
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// [text, button label, where the button goes]
export function nextStep(next) {
  if (!next) return null;
  switch (next.kind) {
    case 'add_parts': return ['Add the parts this RFP covers.', 'Open parts', 'parts'];
    case 'add_suppliers': return ['Add the suppliers you will invite.', 'Open suppliers', 'suppliers'];
    case 'send_letters': return [`Send the RFP invitation letter to ${plural(next.count, 'supplier')}.`,
      'Write RFP invitations', 'suppliers'];
    case 'fix_parts': return [next.message || 'Some parts need a fix before launch.', 'Open parts', 'parts'];
    case 'add_emails': return [`Add an email for ${plural(next.count, 'supplier')}.`, 'Open suppliers', 'suppliers'];
    case 'fix_emails': return [`${plural(next.count, 'email')} already ${next.count === 1 ? 'works' : 'work'} with you for another supplier. Use a different email.`,
      'Open suppliers', 'suppliers'];
    case 'set_offers_due': return [next.passed ? 'Offers due has passed. Choose a new date.' : 'Set when offers are due.',
      'Set offers due', 'offers_due'];
    case 'launch_waiting': return [`Waiting for ${plural(next.count, 'supplier')} to confirm. You can launch without them.`,
      'Launch round 1', 'launch'];
    case 'launch': return ['Ready to launch round 1.', 'Launch round 1', 'launch'];
    case 'round_open': return [`Round ${next.round} is open.${next.at ? ` Offers are due ${formatDueAt(next.at)}.` : ''}`,
      'See bids', 'offers'];
    case 'next_round': return [`Round ${next.round - 1} is closed. Give feedback and open round ${next.round} when you are ready.`,
      'Feedback and next round', 'round'];
    case 'review_scenarios': return ['All rounds are closed. Review the scenarios to award.', 'Review scenarios', 'scenarios'];
    case 'awarded': return ['This RFP is awarded.', 'See the award', 'award'];
    default: return null;
  }
}

function Check() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
      <path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const STATE_WORDS = { done: 'done', current: 'current step', upcoming: 'not started' };

export default function InvitationTracker({ tracker, onGo }) {
  if (!tracker) return null;
  const next = nextStep(tracker.next);
  return (
    <section className="srce-card2 srce-trk" data-testid="srce-tracker" aria-label="RFP progress">
      <div className="srce-trk-scroll">
        <ol className="srce-trk-steps" style={{ gridTemplateColumns: `repeat(${tracker.steps.length}, minmax(72px, 1fr))` }}>
          {tracker.steps.map((s, i) => {
            const sub = subText(s.sub);
            return (
              <li key={s.key} className={`srce-trk-step srce-trk-step--${s.state}`}
                aria-current={s.state === 'current' ? 'step' : undefined}>
                <span className="srce-trk-dot">{s.state === 'done' ? <Check /> : i + 1}</span>
                <span className="srce-trk-l">{s.label}</span>
                <span className="srce-trk-s" title={sub}>{sub || ' '}</span>
                <span className="srce-sr">{`, ${STATE_WORDS[s.state]}`}</span>
              </li>
            );
          })}
        </ol>
      </div>
      {next && (
        <div className="srce-trk-next">
          <span className="srce-trk-k">Next step</span>
          <span className="srce-trk-t">{next[0]}</span>
          <button type="button" className="srce-btn srce-btn--primary srce-btn--sm srce-push" onClick={() => onGo(next[2])}>{next[1]}</button>
        </div>
      )}
    </section>
  );
}
